import {ContentBlocks_ImageTextModule as ImageTextModuleType} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/content_blocks.types';
import {
  Alignment,
  ContentBlockMedia,
  ContentBlockText,
  desktopAndUp,
  RowSection,
  tabletAndUp,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React from 'react';
import sc, {css} from 'styled-components';
import {useContentBlockPromotionTrackingImpression} from '../helpers/useContentBlockPromotionTrackingImpression';
import {useTrackVideoPromotion} from '../helpers/useTrackVideoPromotion';
import {ContentWrapper as TextContentWrapper} from '../Text/Text';
import {
  alignmentMap,
  fontMap,
  imageTextLayoutMap,
  transformImageVideo,
} from '../transforms';

export const ImageTextModule: React.FC<{
  content: ImageTextModuleType;
  blockId?: number;
  testId?: string;
}> = ({content, blockId, testId = 'customPageImageTextModule'}) => {
  const {placementRef, trackFeatureOnVideoEnds, videoElementRef} =
    useTrackContentBlock(content, blockId);

  return (
    <BlockWrapper
      $backgroundColor={content.backgroundColor}
      centerContent
      data-testid={testId}
      data-content-block={blockId}
      ref={placementRef}
    >
      <ContentWrapper>
        {content.headline && content.headline.text !== '' && (
          <StyledHeadline
            text={content.headline.text}
            color={content.headline.color}
            alignment={alignmentMap[content.headline.alignment]}
            size={fontMap[content.headline.size]}
            weight="bold"
            testId={`${testId}Headline`}
          />
        )}

        <TextImageWrapper
          imageFirst={imageTextLayoutMap[content.layout] === 'image-text'}
        >
          <TextColumn isMediaShown={!!content.imageVideo.imageOrVideo}>
            <StyledText
              alignment={alignmentMap[content.text.alignment]}
              color={content.text.color}
              size={fontMap[content.text.size]}
              text={content.text.text}
              testId={`${testId}Text`}
              isAParagraph
            />
          </TextColumn>
          {content.imageVideo.imageOrVideo && (
            <ImageColumn>
              <ContentBlockMedia
                imageOrVideo={transformImageVideo(content.imageVideo)!}
                testId={`${testId}Media`}
                videoRef={videoElementRef}
                onEndVideoCallback={trackFeatureOnVideoEnds}
              />
            </ImageColumn>
          )}
        </TextImageWrapper>
      </ContentWrapper>
    </BlockWrapper>
  );
};

const useTrackContentBlock = (
  content: ImageTextModuleType,
  blockId?: number,
) => {
  // using the fallback id for the promotionId since the double tile
  const promotionId = blockId ? String(blockId) : '';
  const {promotionName, imageTileCTALink} = getPlacementTrackingData(content);
  const locationBlockType = 'imageTextModule';

  const {elementRef: videoElementRef, trackFeatureOnVideoEnds} =
    useTrackVideoPromotion({
      blockId,
      promotionId,
      promotionName,
      locationBlockType,
      path: {
        external: false,
        url: 'video',
      },
    });

  const {elementRef: placementRef} =
    useContentBlockPromotionTrackingImpression<HTMLDivElement>({
      blockId,
      promotionId,
      promotionName,
      locationBlockType,
      path: imageTileCTALink,
      contentBlockSource: 'banner',
    });

  return {
    placementRef,
    videoElementRef,
    trackFeatureOnVideoEnds,
  };
};

const getPlacementTrackingData = (content: ImageTextModuleType) => {
  const promotionName = (() => {
    if (content.firstCta) {
      return content.firstCta.text.text;
    } else if (content.secondCta) {
      return content.secondCta.text.text;
    } else if (content.headline) {
      return content.headline.text;
    }

    return '';
  })();

  const imageTileCTALink = (() => {
    if (content.imageVideo.imageOrVideo?.$case === 'image') {
      if (content.firstCta) {
        return content.firstCta.link;
      } else if (content.secondCta) {
        return content.secondCta.link;
      }
    }

    return {
      url: '',
      external: false,
    };
  })();

  return {
    promotionName,
    imageTileCTALink,
  };
};

const BlockWrapper = linariaStyled(RowSection)<{
  $backgroundColor?: string;
}>`
  background-color: ${({$backgroundColor}) => $backgroundColor ?? ''};
  padding-bottom: 4rem;
`;

const ContentWrapper = sc.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
`;

const TextImageWrapper = sc.div<{imageFirst: boolean}>`
  align-items: center;
  display: flex;
  flex-basis: 100%;
  flex-direction: column;
  gap: 2rem;
  max-width: 1440px;

  ${tabletAndUp(
    css`
      flex-direction: row;
    `,
  )}

  ${desktopAndUp(
    css`
      gap: 9rem;
    `,
  )}

  ${({imageFirst}) =>
    imageFirst &&
    css`
      flex-direction: column-reverse;
      ${tabletAndUp(
        css`
          flex-direction: row-reverse;
        `,
      )}
    `}
`;

const TextColumn = sc.div<{isMediaShown: boolean}>`
  width: 100%;

  ${({isMediaShown}) =>
    isMediaShown &&
    tabletAndUp(
      css`
        width: 60%;
      `,
    )}
`;

const ImageColumn = sc.div`
  width: 100%;

  ${tabletAndUp(
    css`
      width: 40%;
    `,
  )}
`;

const StyledText = sc(ContentBlockText)<{alignment: Alignment}>`
  justify-content: start;
  padding: 0;
  text-align: ${({alignment}) => alignment};

  ${TextContentWrapper} {
    max-width: 100%;
    padding-bottom: 1rem;
  }
`;

const StyledHeadline = sc(ContentBlockText)`
  margin-bottom: 2rem;
`;
